import { Button, Form, Modal, Select, Space } from 'antd'
import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCheck, faLink, faSyncAlt, faUnlink } from '@fortawesome/pro-solid-svg-icons'
import { QboCustomer, QboCustomerProperty, QboLinkResult } from '../../domain/quickbooks/quickbooksModel'
import {
  useQboCustomersQuery,
  useLinkCustomerToQboMutation,
  useUnlinkCustomerFromQboMutation,
  usePushCustomerToQboMutation,
} from '../../domain/quickbooks/quickbooksQueries'
import QboEntityPickerModal from './QboEntityPickerModal'

type Props = {
  customerId: number | undefined
  customerDisplayName: string
  currentQboId: string | undefined
  currentQboDisplayName: string | undefined
  /** Every property the customer has. When there is more than one, pushing to
   *  QuickBooks asks which one to use for the shipping address first. */
  propertyList?: QboCustomerProperty[]
  onSuccess: (result: QboLinkResult) => void
  onClose: () => void
}

const LinkCustomerToQuickBooks = ({
  customerId,
  customerDisplayName,
  currentQboId,
  currentQboDisplayName,
  propertyList,
  onSuccess,
  onClose,
}: Props) => {
  const open = customerId !== undefined
  const [selectedItem, setSelectedItem] = useState<QboCustomer | null>(null)
  const [isSelectingProperty, setIsSelectingProperty] = useState(false)
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | undefined>()

  const properties = propertyList ?? []
  const needsPropertySelection = properties.length > 1

  const { data: qboCustomers, isLoading, refetch } = useQboCustomersQuery()

  const linkMutation = useLinkCustomerToQboMutation((result) => {
    onSuccess(result)
    onClose()
  })

  const unlinkMutation = useUnlinkCustomerFromQboMutation(() => {
    onSuccess({ qboId: '', qboDisplayName: '' })
    onClose()
  })

  const pushMutation = usePushCustomerToQboMutation((result) => {
    setIsSelectingProperty(false)
    onSuccess(result)
    onClose()
  })

  useEffect(() => {
    if (open && qboCustomers && currentQboId) {
      const existing = qboCustomers.find((c) => c.id === currentQboId)
      setSelectedItem(existing ?? null)
    } else {
      setSelectedItem(null)
    }
  }, [open, qboCustomers, currentQboId])

  useEffect(() => {
    if (!open) {
      setIsSelectingProperty(false)
    }
    setSelectedPropertyId(properties[0]?.id)
  }, [open, propertyList])

  const handleLink = () => {
    if (!selectedItem || customerId === undefined) return
    linkMutation.mutate({
      id: customerId,
      qboId: selectedItem.id,
      qboDisplayName: selectedItem.displayName,
    })
  }

  const handleUnlink = () => {
    if (customerId === undefined) return
    unlinkMutation.mutate(customerId)
  }

  const handlePush = () => {
    if (customerId === undefined) {
      return
    }
    if (needsPropertySelection) {
      setIsSelectingProperty(true)
      return
    }
    pushMutation.mutate({ customerId })
  }

  const handlePushWithSelectedProperty = () => {
    if (customerId === undefined) {
      return
    }
    pushMutation.mutate({ customerId, selectedPropertyId })
  }

  const isMutating = linkMutation.isLoading || unlinkMutation.isLoading || pushMutation.isLoading

  const headerContent = (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      {currentQboId ? (
        <>
          <Button
            danger
            size="large"
            shape="round"
            icon={<FontAwesomeIcon icon={faUnlink} />}
            onClick={handleUnlink}
            loading={unlinkMutation.isLoading}
            disabled={isMutating}
          >
            Un-link this Customer from QuickBooks
          </Button>
          <p style={{ color: '#999', marginTop: 8 }}>
            or search for and link this customer to a<br />different customer in your QuickBooks account
          </p>
        </>
      ) : (
        <>
          <Button
            type="primary"
            size="large"
            shape="round"
            icon={<FontAwesomeIcon icon={faSyncAlt} />}
            onClick={handlePush}
            loading={pushMutation.isLoading}
            disabled={isMutating}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Push this Customer to QuickBooks
          </Button>
          <p style={{ color: '#999', marginTop: 8 }}>
            or search for and link this customer to an<br />existing customer in your QuickBooks account
          </p>
        </>
      )}
    </div>
  )

  return (
    <>
    <Modal
      title="Select which property to be used for QuickBooks shipping address"
      open={open && isSelectingProperty}
      onCancel={() => setIsSelectingProperty(false)}
      footer={
        <>
          <Button onClick={() => setIsSelectingProperty(false)} icon={<FontAwesomeIcon icon={faArrowLeft} />}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={handlePushWithSelectedProperty}
            disabled={selectedPropertyId === undefined || pushMutation.isLoading}
            loading={pushMutation.isLoading}
            icon={<FontAwesomeIcon icon={faCheck} />}
          >
            Select
          </Button>
        </>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Property">
          <div data-testid="qbo-push-property">
            <Select
              value={selectedPropertyId}
              onChange={setSelectedPropertyId}
              options={properties.map(p => ({ label: p.address1, value: p.id }))}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>

    <QboEntityPickerModal
      open={open && !isSelectingProperty}
      onCancel={onClose}
      title={`${customerDisplayName} QuickBooks Link`}
      searchPlaceholder="search QuickBooks customers..."
      items={qboCustomers}
      isLoading={isLoading}
      initialSearchFilter={currentQboDisplayName}
      selectedItemId={selectedItem?.id}
      onSelectItem={setSelectedItem}
      renderItemLabel={(item) => (
        <Space>
          {(item.id === currentQboId || item.id === selectedItem?.id) && (
            <FontAwesomeIcon icon={faLink} />
          )}
          <span>
            {item.displayName}
            {item.companyName && item.companyName !== item.displayName && (
              <span> - {item.companyName}</span>
            )}
          </span>
        </Space>
      )}
      headerContent={headerContent}
      footerExtra={
        <Button
          onClick={handleLink}
          disabled={!selectedItem || isMutating}
          loading={linkMutation.isLoading}
          icon={<FontAwesomeIcon icon={faCheck} />}
        >
          Link
        </Button>
      }
      onOpen={() => refetch()}
    />
    </>
  )
}

export default LinkCustomerToQuickBooks
